import * as React from "react";
import { Button } from "@/taskpane/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/taskpane/components/ui/tooltip";
import {
  FileText,
  MessagesSquare,
  PenSquare,
  Copy,
  Search,
  Mic,
  FileSearch,

  Unlock,
} from "lucide-react";
import OnboardingHeader from "./OnboardingHeader";

interface IntroProps {
  onGetStarted: () => void;
}

const featureItems = [
  {
    icon: <FileText size={16} />,
    title: "Document Review",
    description:
      "Quickly identify ambiguities, complex language, and potential issues in legal documents with AI-powered analysis.",
  },
  {
    icon: <MessagesSquare size={16} />,
    title: "AI Chat Assistant",
    description:
      "Interactive AI chat for brainstorming ideas, drafting content, and getting instant legal insights.",
  },
  {
    icon: <PenSquare size={16} />,
    title: "Smart Drafting",
    description:
      "AI-assisted clause and definition drafting that learns your writing style and preferences.",
  },
  {
    icon: <Copy size={16} />,
    title: "Multi-Document Analysis",
    description:
      "Find inconsistencies, conflicts, and gaps across multiple documents simultaneously.",
  },
  {
    icon: <Search size={16} />,
    title: "Legal Research",
    description:
      "Access comprehensive Australian & New Zealand law databases, cases, and legal precedents.",
  },
  {
    icon: <FileSearch size={16} />,
    title: "Adaptive Clauses",
    description:
      "Generate and manage clauses that match your unique legal writing voice and firm standards.",
  },
  {
    icon: <Mic size={16} />,
    title: "Voice to Text",
    description:
      "Convert speech to actionable text including emails, summaries, and legal documents.",
  },
  {
    icon: <FileSearch size={16} />,
    title: "Discrepancy Detection",
    description:
      "Automatically detect document variances, oversights, and potential compliance issues.",
  },
];

const Intro: React.FC<IntroProps> = ({ onGetStarted }) => {
  return (
    <div className="flex flex-col gap-4 bg-white w-full max-w-md mx-auto">
      <OnboardingHeader />

      <div className="flex flex-col gap-8 w-full">
        <div className="text-center gap-2 flex flex-col w-full">
          <h1 className="text-xl font-medium text-neutral-200 mb-2 text-center">
            AI Legal Assistant
          </h1>
          <p className="text-sm font-normal text-neutral-400 text-center">
            Streamline your legal workflow with intelligent document analysis, AI powered research,
            and automated drafting tools designed by legal professionals, for legal professionals.
          </p>
        </div>

        <div className="w-full flex flex-col gap-4">
          <h2 className="text-base font-medium text-neutral-200 text-center">
            What you can do with Legal Assistant AI
          </h2>

          <div className="flex flex-wrap gap-2 justify-center w-full">
            {featureItems.map((feature, index) => (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="px-2.5 py-1.5 rounded-lg border border-border flex items-center gap-1.5 cursor-pointer transition-all hover:bg-gray-100/50"
                      tabIndex={0}
                      role="button"
                    >
                      <span className="text-primary-300">{feature.icon}</span>
                      <p className="text-xs font-medium text-primary-300">{feature.title}</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent sideOffset={8} className="max-w-[260px]">
                    <p>{feature.description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center gap-6 w-full">
          <p className="text-sm font-normal text-neutral-200 text-center">
            Ready to transform your legal practice?
          </p>
          <Button onClick={onGetStarted}>
            <Unlock className="mr-2 h-4 w-4 text-neutral-white" /> Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Intro;
